import React, { useMemo, useState } from 'react';
import Pagination from './Pagination.jsx';
import SectionItem from './SectionItem.jsx';
import AppraisalFields from './AppraisalFields.jsx';
import { useFormik } from 'formik';
import useUser from 'hooks/useUser';
import { PropTypes } from 'prop-types';
import styled, { css } from 'styled-components';
import { type_bma_v1, type_bma_v2 } from './typeMapping';
import { compareVersions } from 'compare-versions';
import { useEffect, useCallback } from 'react';
import { Button } from 'flowbite-react';
import {
  useVerifyDocument,
  useAcquireLockDocument,
  useUnverifyDocument,
  useReleaseLockDocument,
  useUpdateDocument,
} from 'hooks';
import { CONFIDENCE_SCORE } from 'configs/confidence.js';

const StyledSection = styled.div`
  ${({ height }) =>
    css`
      overflow-y: scroll;
      height: calc(100% - ${height}px);
    `}
`;

const StyledButton = styled.div`
  & button {
    width: 100%;
    background-color: #1c64f2;
  }
`;

const StyledVerifyButton = styled.div`
  width: 60px;
  height: 30px;
  border-radius: 0.375rem;
  & button {
    height: 30px;
    background-color: #3f83f8;
  }
`;

const StyledEditButton = styled.div`
  width: 60px;
  height: 30px;
  border-radius: 0.375rem;
  & button {
    height: 30px;

    border: 1px solid #3f83f8;
  }
  & span {
    color: #3f83f8;
  }
`;

const APPRAISAL_CONTENT_SCHEMAS = ['appraisal_sections', 'type_appraisal_v1'];
const APPRAISAL_DOCUMENT_TYPE_ID = 40;

const getBmaSectionReference = section => section?.reference ?? section?.Reference;

const getBmaSectionElements = section => {
  const rawElements = section?.elements ?? section?.Elements;
  if (!Array.isArray(rawElements)) {
    return [];
  }

  return rawElements.map(element => ({
    ...element,
    index: element?.index ?? element?.Index,
    displayText: element?.displayText ?? element?.DisplayText,
    shortDescription: element?.shortDescription ?? element?.ShortDescription,
    value: element?.value ?? element?.Value,
    confidence: element?.confidence ?? element?.Confidence,
  }));
};

const normalizeBmaSection = section => ({
  ...section,
  reference: getBmaSectionReference(section),
  sectionTitle: section?.sectionTitle ?? section?.SectionTitle,
  elements: getBmaSectionElements(section),
});

const sortBmaElements = elements =>
  [...elements]
    .filter(item => item?.index !== null && item?.index !== undefined)
    .sort((a, b) => compareVersions(a?.index, b?.index));

const resolveBmaV2MappingPage = (page, fallbackIndex) => {
  const bmaPage = page?.bmaPage;
  if (bmaPage !== undefined && bmaPage !== null) {
    const numericBmaPage = Number(bmaPage);
    if (type_bma_v2[numericBmaPage]) {
      return numericBmaPage;
    }
    if (type_bma_v2[numericBmaPage + 1]) {
      return numericBmaPage + 1;
    }
  }
  return fallbackIndex + 1;
};

// Fallback map for documents extracted before pdfPage was added to the API output.
// Keys match the section ids emitted by AppraisalSqlWriter.BuildTypeAppraisalV1FromTransformedBody.
// ERC Summary labelled base layout: template §6 (listing stats) and §7 (competing grid) are never the same PDF page.
const APPRAISAL_SECTION_PDF_PAGE = {
  subject_property:    1,
  valuation:           1,
  subject_improvements:1,
  property_context:    2,
  site_and_neighborhood: 2,
  improvements_overview: 3,
  market_listing_statistics: 6,
  market_analysis:     7,
  forecasting:         8,
  comparable_summary:  9,
  certification:       9,
};

function effectiveAppraisalSectionPdfPage(section) {
  return section.pdfPage ?? APPRAISAL_SECTION_PDF_PAGE[section.id] ?? 1;
}

/**
 * Per-field `page` (when set) is the PDF page where that value appears; it can differ
 * from the section's `pdfPage`. Only groups/fields visible on `pdfPage` are kept.
 *
 * Fields with `page = null` (e.g. extracted via grid fallback with no DI page number)
 * are shown alongside any sibling in the same group that is explicitly on `pdfPage`.
 * If no sibling matches, they fall back to the section's effective PDF page. This
 * prevents null-page fields from landing on an unreachable page while all their
 * siblings are shown on a different page.
 */
function filterAppraisalSectionForPdfPage(section, pdfPage) {
  const sectionPage = effectiveAppraisalSectionPdfPage(section);
  const groups = (section.groups || [])
    .map((group, groupIndex) => {
      const indexed = (group.fields || []).map((field, fieldIndex) => ({ field, fieldIndex }));
      const hasExplicitMatch = indexed.some(
        ({ field }) => field.page != null && Number.isFinite(field.page) && field.page === pdfPage,
      );
      const fields = indexed
        .filter(({ field }) => {
          if (field.page != null && Number.isFinite(field.page)) {
            return field.page === pdfPage;
          }
          // Null-page field: show alongside explicit siblings when present, else
          // fall back to section page.
          return hasExplicitMatch || sectionPage === pdfPage;
        })
        .map(({ field, fieldIndex }) => ({
          ...field,
          _formikFieldIndex: fieldIndex,
        }));

      if (!fields.length) {
        return null;
      }

      return { ...group, _formikGroupIndex: groupIndex, fields };
    })
    .filter(Boolean);

  if (!groups.length) {
    return null;
  }

  return { ...section, groups };
}

/** Verify UI step (1–6): ERC sections (1,2), (3,4), 5, 6, (7,8), (9,10). Requires API `verificationStep` on fields. */
function filterAppraisalSectionForVerificationStep(section, verificationStep) {
  const groups = (section.groups || [])
    .map((group, groupIndex) => {
      const fields = (group.fields || [])
        .map((field, fieldIndex) => ({ field, fieldIndex }))
        .filter(({ field }) => Number(field?.verificationStep) === verificationStep)
        .map(({ field, fieldIndex }) => ({
          ...field,
          _formikFieldIndex: fieldIndex,
        }));

      if (!fields.length) {
        return null;
      }

      return { ...group, _formikGroupIndex: groupIndex, fields };
    })
    .filter(Boolean);

  if (!groups.length) {
    return null;
  }

  return { ...section, groups };
}

/** When the PDF page has no extracted content, pick the closest page that does. */
function nearestNavigablePage(current, sortedPages) {
  if (!sortedPages.length || sortedPages.includes(current)) {
    return null;
  }
  const lower = sortedPages.filter(p => p < current);
  const upper = sortedPages.filter(p => p > current);
  if (lower.length && upper.length) {
    const prev = lower[lower.length - 1];
    const next = upper[0];
    return current - prev <= next - current ? prev : next;
  }
  if (lower.length) {
    return lower[lower.length - 1];
  }
  return upper[0];
}

/** Minimum PDF `page` among fields on this verification step; default 1. */
function computeAppraisalAnchorPdfPage(sections, verificationStep) {
  let min = Infinity;
  (sections || []).forEach(s => {
    (s.groups || []).forEach(g => {
      (g.fields || []).forEach(f => {
        if (Number(f?.verificationStep) !== verificationStep) {
          return;
        }
        if (f.page != null && Number.isFinite(f.page) && f.page >= 1) {
          min = Math.min(min, f.page);
        }
      });
    });
  });
  return Number.isFinite(min) ? min : 1;
}

const Section = ({
  selectedAgent = {
    contents: [],
  },
  currentPage,
  setCurrentPage,
  totalPage,
  lockedByAnotherUser,
  isLocked,
  setIsLocked,
  bmaVersion,
}) => {
  const { userToken } = useUser();

  const [enableVerify, setEnableVerify] = useState(false);
  const [enableRenderInput, setEnableRenderInput] = useState(true);
  const [checkedLowConfidence, setCheckedLowConfidence] = useState(false);
  const [countLowConfidence, setCountLowConfidence] = useState({});
  /** ERC-grouped verify pages (1–6); PDF page stays in `currentPage` from parent. */
  const [verificationStep, setVerificationStep] = useState(1);

  const { acquireLockDocument, isLoading } = useAcquireLockDocument(
    setEnableVerify,
    setEnableRenderInput,
  );

  const { unverify } = useUnverifyDocument(
    acquireLockDocument,
    selectedAgent?.id,
    userToken,
  );

  const { releaseLockDocument } = useReleaseLockDocument();

  const { verify } = useVerifyDocument();

  const { update } = useUpdateDocument(verify, selectedAgent.id, userToken);
  const isAppraisalDocument =
    APPRAISAL_CONTENT_SCHEMAS.includes(selectedAgent?.contentsSchema) ||
    selectedAgent?.reswareDocumentTypeId === APPRAISAL_DOCUMENT_TYPE_ID;

  const temp_bma_v1 = (item, currentPage) => {
    const normalizedItem = normalizeBmaSection(item);
    if (!normalizedItem || typeof normalizedItem !== 'object') {
      return null;
    }

    const convertSection = { ...normalizedItem, elements: [] };
    const pageMap = type_bma_v1[currentPage];

    if (!pageMap || !pageMap.sections) {
      convertSection.elements = sortBmaElements(normalizedItem.elements);
      return convertSection;
    }

    let targetMap = {};
    Object.keys(pageMap.sections).forEach(itemPageMapped => {
      if (pageMap.sections[itemPageMapped]?.reference === normalizedItem.reference) {
        targetMap = pageMap.sections[itemPageMapped];
      }
    });

    if (
      currentPage === 4 ||
      currentPage === 5 ||
      normalizedItem.reference === 9 ||
      normalizedItem.reference === 10
    ) {
      targetMap = pageMap.sections;
    }

    if (!targetMap || typeof targetMap !== 'object' || Object.keys(targetMap).length === 0) {
      convertSection.elements = sortBmaElements(normalizedItem.elements);
      return convertSection;
    }

    const listElement = [...normalizedItem.elements];

    let sortList = [];

    if (currentPage === 2 || normalizedItem.reference === 5) {
      const fieldsMapping = targetMap.fields || {};
      Object.keys(fieldsMapping).forEach(filedName => {
        const element = fieldsMapping[filedName];

        if (element && element.type === 'group') {
          const found = Object.keys(element.values || {}).some(x =>
            listElement.some(y => y.index === x),
          );

          if (found === true) {
            listElement.push({
              index: filedName,
              label: element.label,
              displayText: element.label,
              type: 'group',
            });
          }
        }
      });

      sortList = listElement
        .filter(item => item?.index !== null && item?.index !== undefined)
        .sort(function (a, b) {
          return compareVersions(a?.index, b?.index);
        });

      sortList.forEach((itemSort, indexItemSort) => {
        if (indexItemSort > 0) {
          if (itemSort.type === 'group') {
            const targetItemNew = { ...sortList[indexItemSort - 1], haveUnderLine: true };
            Object.assign(sortList[indexItemSort - 1], targetItemNew);
          }
        }
      });
    } else if (
      currentPage === 4 ||
      currentPage === 5 ||
      normalizedItem.reference === 9 ||
      normalizedItem.reference === 10
    ) {
      const testTemp = listElement.filter(x => (x?.index?.match(/\./g) || []).length >= 2);
      Object.keys(targetMap).forEach(sectionKey => {
        const fieldsMapping = targetMap[sectionKey]?.fields || {};
        Object.keys(fieldsMapping).forEach(filedName => {
          const element = fieldsMapping[filedName];
          if (
            element &&
            element.type === 'group' &&
            testTemp.find(x => x.index.includes(filedName))
          ) {
            listElement.push({
              index: filedName,
              label: element.label,
              displayText: element.label,
              type: 'group',
            });
          }

          const targetItem = listElement.find(x => x.index === filedName);

          if (targetItem) {
            const targetItemNew = { ...targetItem, haveUnderLine: true };
            Object.assign(targetItem, targetItemNew);
          }
        });
      });

      sortList = listElement
        .filter(item => item?.index !== null && item?.index !== undefined)
        .sort(function (a, b) {
          return compareVersions(a?.index, b?.index);
        });
    } else {
      const testTemp = listElement?.filter(
        x => (x?.index?.match(/\./g) || []).length >= 2,
      );
      const fieldsMapping = targetMap.fields || {};
      Object.keys(fieldsMapping).forEach(filedName => {
        const element = fieldsMapping[filedName];
        if (
          element &&
          element.type === 'group' &&
          testTemp.find(x => x.index.includes(filedName))
        ) {
          listElement.push({
            index: filedName,
            label: element.label,
            displayText: element.label,
            type: 'group',
          });
        }

        const targetItem = listElement.find(x => x.index === filedName);

        if (targetItem) {
          const targetItemNew = { ...targetItem, haveUnderLine: true };
          Object.assign(targetItem, targetItemNew);
        }
      });

      sortList = listElement
        .filter(item => item?.index !== null && item?.index !== undefined)
        .sort(function (a, b) {
          return compareVersions(a?.index, b?.index);
        });
    }

    if (sortList.length > 0) {
      const lastItem = sortList[sortList.length - 1];
      const targetItemNew = { ...lastItem, haveUnderLine: true };
      Object.assign(lastItem, targetItemNew);
    }
    convertSection.elements = sortList;

    return convertSection;
  };

  const temp_bma_v2 = (item, currentPage) => {
    const normalizedItem = normalizeBmaSection(item);
    if (!normalizedItem || typeof normalizedItem !== 'object') {
      return null;
    }

    if (!type_bma_v2 || !type_bma_v2[currentPage]) {
      return {
        ...normalizedItem,
        elements: sortBmaElements(normalizedItem.elements),
      };
    }

    const convertSection = { ...normalizedItem, elements: [] };
    const pageMap = type_bma_v2[currentPage];

    if (!pageMap || !pageMap.sections) {
      convertSection.elements = sortBmaElements(normalizedItem.elements);
      return convertSection;
    }

    let targetMap = {};
    Object.keys(pageMap.sections).forEach((itemPageMapped) => {
      if (pageMap.sections[itemPageMapped]?.reference === normalizedItem.reference) {
        targetMap = pageMap.sections[itemPageMapped];
      }
    });

    if (
      currentPage === 4 ||
      currentPage === 5 ||
      normalizedItem.reference === 9 ||
      normalizedItem.reference === 10
    ) {
      targetMap = pageMap.sections;
    }

    if (!targetMap || typeof targetMap !== 'object' || Object.keys(targetMap).length === 0) {
      convertSection.elements = sortBmaElements(normalizedItem.elements);
      return convertSection;
    }

    const fieldsMapping = targetMap.fields ? targetMap.fields : targetMap;

    if (!fieldsMapping || typeof fieldsMapping !== 'object') {
      convertSection.elements = sortBmaElements(normalizedItem.elements);
      return convertSection;
    }

    const listElement = [...normalizedItem.elements];
    let sortList = [];

    if (currentPage === 2 || normalizedItem.reference === 5) {
      Object.keys(fieldsMapping).forEach((fieldName) => {
        const element = fieldsMapping[fieldName];
        if (element && element.type === 'group') {
          const found = Object.keys(element.values || {}).some((x) =>
            listElement.some((y) => y.index === x)
          );
          if (found) {
            listElement.push({
              index: fieldName,
              label: element.label,
              displayText: element.label,
              type: 'group',
            });
          }
        }
      });
    } else {
      Object.keys(fieldsMapping).forEach((fieldName) => {
        const element = fieldsMapping[fieldName];
        if (element && element.type === 'group') {
          const found = Object.keys(element.values || {}).some((x) =>
            listElement.some((y) => y.index === x)
          );
          if (found) {
            listElement.push({
              index: fieldName,
              label: element.label,
              displayText: element.label,
              type: 'group',
            });
          }
        }
      });
    }

    sortList = sortBmaElements(listElement);

    sortList.forEach((itemSort, indexItemSort) => {
      if (indexItemSort > 0 && itemSort.type === 'group') {
        sortList[indexItemSort - 1] = {
          ...sortList[indexItemSort - 1],
          haveUnderLine: true,
        };
      }
    });

    convertSection.elements = sortList;
    return convertSection;
  };
  


  const initialValues = useMemo(() => {
    if (isAppraisalDocument || !Array.isArray(selectedAgent?.contents)) {
      return [];
    }

    return selectedAgent.contents.map((page, key) => {
    // Normalize API response shape (some endpoints return PascalCase keys)
    const normalizedPage = {
      ...page,
      page: page?.page ?? page?.Page ?? key,
      bmaPage: page?.bmaPage ?? page?.BMAPage,
      sections: page?.sections ?? page?.Sections ?? [],
    };

    const pageConverted = bmaVersion === 1
      ? key + 1
      : resolveBmaV2MappingPage(normalizedPage, key);

    const page2 = {
      ...normalizedPage,
      sections: normalizedPage?.sections
        ?.map((item) => {
          if (!item) {
            return null;
          }

          const transformedItem = bmaVersion === 1 
            ? temp_bma_v1(item, pageConverted) 
            : temp_bma_v2(item, pageConverted);

          if (!transformedItem) {
            return null;
          }

          return transformedItem;
        })
        .filter(Boolean)
        .map((section) => {
          if (!section?.elements) {
            return null;
          }

          const hasLowConfidence = section.elements.some(
            el => el.confidence && el.confidence < CONFIDENCE_SCORE
          ) || false; 

          return { ...section, hasLowConfidence };
        })
        .filter(Boolean),
    };

      return page2;
    });
  }, [isAppraisalDocument, selectedAgent?.contents, bmaVersion]);

  const bmaNavSequence = useMemo(() => {
    if (isAppraisalDocument) {
      return [];
    }

    const pages = new Set();
    initialValues.forEach((pageData, index) => {
      const pdfPageIndex = pageData?.page ?? index;
      const numericPdfPageIndex = Number(pdfPageIndex);

      if (Number.isFinite(numericPdfPageIndex) && numericPdfPageIndex >= 0) {
        pages.add(numericPdfPageIndex + 1);
      }
    });

    return [...pages].sort((a, b) => a - b);
  }, [initialValues, isAppraisalDocument]);

  const appraisalInitialValues = useMemo(() => {
    if (!isAppraisalDocument) {
      return { schema: 'type_appraisal_v1', sections: [] };
    }

    return selectedAgent?.contents || { schema: 'type_appraisal_v1', sections: [] };
  }, [isAppraisalDocument, selectedAgent?.contents]);

  const appraisalSections = useMemo(() => {
    const sections = appraisalInitialValues?.sections || [];

    return [...sections].sort((a, b) => (a?.order || 0) - (b?.order || 0));
  }, [appraisalInitialValues]);

  const useVerificationSteps = useMemo(() => {
    if (!isAppraisalDocument) {
      return false;
    }
    let sawField = false;
    let allKeyed = true;
    appraisalSections.forEach(s => {
      (s.groups || []).forEach(g => {
        (g.fields || []).forEach(f => {
          if (!f) {
            return;
          }
          sawField = true;
          if (f.verificationStep == null || !Number.isFinite(Number(f.verificationStep))) {
            allKeyed = false;
          }
        });
      });
    });
    return sawField && allKeyed;
  }, [isAppraisalDocument, appraisalSections]);

  const visibleAppraisalSections = useMemo(() => {
    if (!isAppraisalDocument) {
      return [];
    }

    return appraisalSections
      .map((section, index) => ({ ...section, _arrayIndex: index }))
      .map(s =>
        useVerificationSteps
          ? filterAppraisalSectionForVerificationStep(s, verificationStep)
          : filterAppraisalSectionForPdfPage(s, currentPage),
      )
      .filter(Boolean);
  }, [
    currentPage,
    verificationStep,
    appraisalSections,
    isAppraisalDocument,
    useVerificationSteps,
  ]);

  /**
   * Pagination sequence: verification steps 1–6 when API sends `verificationStep`,
   * otherwise sorted unique PDF pages (legacy).
   */
  const appraisalNavSequence = useMemo(() => {
    if (!isAppraisalDocument) {
      return [];
    }
    if (useVerificationSteps) {
      const steps = new Set();
      appraisalSections.forEach(s => {
        (s.groups || []).forEach(g => {
          (g.fields || []).forEach(f => {
            if (f?.verificationStep != null && Number.isFinite(Number(f.verificationStep))) {
              steps.add(Number(f.verificationStep));
            }
          });
        });
      });
      return [...steps].sort((a, b) => a - b);
    }
    const pages = new Set();
    appraisalSections.forEach(s => {
      pages.add(effectiveAppraisalSectionPdfPage(s));
      (s.groups || []).forEach(g => {
        (g.fields || []).forEach(f => {
          if (f.page != null && Number.isFinite(f.page)) {
            pages.add(f.page);
          }
        });
      });
    });
    return [...pages].sort((a, b) => a - b);
  }, [isAppraisalDocument, appraisalSections, useVerificationSteps]);

  const handleAppraisalVerificationStepChange = useCallback(nextStep => {
    setVerificationStep(nextStep);
  }, []);

  const formik = useFormik({
    initialValues: isAppraisalDocument
      ? appraisalInitialValues
      : {
          contents: initialValues,
        },
    onSubmit: values => {
      if (isAppraisalDocument) {
        update([selectedAgent.id, userToken, { contents: values }]);
        return;
      }

      const convertValues = {
        ...values,
        contents: values.contents.map(page => {
          return {
            ...page,
            sections: page.sections.map(section => {
              return {
                ...section,
                elements: section.elements.filter(element => element?.type !== 'group'),
              };
            }),
          };
        }),
      };
      update([selectedAgent.id, userToken, convertValues]);
    },
    enableReinitialize: true,
  });

  const handleBeginVerification = () => {
    setEnableRenderInput(false);
    acquireLockDocument([selectedAgent.id, userToken]);
  };

  const handleDiscardChanges = () => {
    releaseLockDocument([selectedAgent.id, userToken]);
    formik.resetForm();
  };

  const handleEdit = () => {
    setIsLocked(true);
    unverify([selectedAgent.id, userToken]);
  };

  useEffect(() => {
    if (lockedByAnotherUser) {
      setEnableVerify(true);
    }
  }, [lockedByAnotherUser]);

  useEffect(() => {
    setEnableRenderInput(true);
    if (selectedAgent?.verified || isLocked) {
      setEnableVerify(true);
    } else {
      setEnableVerify(false);
    }
  }, [selectedAgent, isLocked]);

  useEffect(() => {
    setCurrentPage(1);
  }, [checkedLowConfidence, setCurrentPage]);

  useEffect(() => {
    setVerificationStep(1);
  }, [selectedAgent?.id]);

  useEffect(() => {
    if (!isAppraisalDocument || useVerificationSteps || appraisalNavSequence.length === 0) {
      return;
    }
    const target = nearestNavigablePage(currentPage, appraisalNavSequence);
    if (target == null) {
      return;
    }
    setCurrentPage(target);
  }, [
    isAppraisalDocument,
    useVerificationSteps,
    appraisalNavSequence,
    currentPage,
    setCurrentPage,
  ]);

  useEffect(() => {
    if (isAppraisalDocument || bmaNavSequence.length === 0) {
      return;
    }
    const target = nearestNavigablePage(currentPage, bmaNavSequence);
    if (target == null) {
      return;
    }
    setCurrentPage(target);
  }, [
    bmaNavSequence,
    currentPage,
    isAppraisalDocument,
    setCurrentPage,
  ]);

  useEffect(() => {
    if (!isAppraisalDocument || !useVerificationSteps || appraisalNavSequence.length === 0) {
      return;
    }
    const anchor = computeAppraisalAnchorPdfPage(appraisalSections, verificationStep);
    if (Number.isFinite(anchor) && anchor >= 1) {
      setCurrentPage(anchor);
    }
  }, [
    selectedAgent?.id,
    isAppraisalDocument,
    useVerificationSteps,
    verificationStep,
    appraisalSections,
    appraisalNavSequence.length,
    setCurrentPage,
  ]);

  useEffect(() => {
    if (isAppraisalDocument) {
      return;
    }

    initialValues.forEach((item, index) => {
      let count = 0;
      item.sections.forEach(section => {
        const lowConfidence = section.elements.filter(
          el => el.confidence < CONFIDENCE_SCORE,
        );
        count = count + lowConfidence.length;
      });
      // Use the page.page property as the key instead of array index
      const pageKey = item?.page !== undefined && item?.page !== null ? item.page : index;
      setCountLowConfidence(countLowConfidence => ({
        ...countLowConfidence,
        [pageKey]: count,
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues, isAppraisalDocument]);

  return (
    <>
      <div className=" relative flex w-full justify-between pt-5 pl-5 pr-5 pb-2">
        <div>
          <div className="mb-3">{`Agent ${selectedAgent?.partnerCompanyName ?? ''}`}</div>
        </div>
        {!lockedByAnotherUser &&
          enableVerify &&
          enableRenderInput &&
          (selectedAgent?.verified ? (
            <StyledEditButton>
              <Button
                data-testid="test-edit-button"
                disabled={!!lockedByAnotherUser}
                color={'gray'}
                onClick={() => handleEdit()}
              >
                Edit
              </Button>
            </StyledEditButton>
          ) : (
            <StyledVerifyButton>
              <Button
                data-testid="test-verify-button"
                disabled={!enableVerify || !!lockedByAnotherUser}
                onClick={formik.handleSubmit}
              >
                Verify
              </Button>
            </StyledVerifyButton>
          ))}
      </div>
      {enableVerify && !lockedByAnotherUser && !isAppraisalDocument && (
        <div className="relative  flex items-start mb-5 ml-5 mr-5">
          <label className="inline-flex  min-w-11 min-w-11 cursor-pointer">
            <input
              type="checkbox"
              value=""
              className="sr-only peer"
              data-testid="checkLowConfidence"
              checked={checkedLowConfidence}
              onChange={() => setCheckedLowConfidence(!checkedLowConfidence)}
            />
            <div
              className="w-11 min-w-11 h-6 c bg-gray-200
                peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full
                peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] 
                after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 
                after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"
            />
            <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
              Low-Confidence Fields Only
            </span>
          </label>
        </div>
      )}
      <hr />

      {!isLocked && !enableVerify && !selectedAgent?.verified && !isLoading && (
        <div className="flex justify-center pt-3">
          <StyledButton>
            <Button
              data-testid="test-begin-verification-button"
              onClick={() => handleBeginVerification()}
            >
              Begin Verification
            </Button>
          </StyledButton>
        </div>
      )}

      {isLoading && !enableVerify && (
        <span className="w-full flex justify-center">Loading...</span>
      )}

      {enableVerify &&
        enableRenderInput &&
        (!lockedByAnotherUser ? (
          isAppraisalDocument ? (
            <>
              {appraisalNavSequence.length > 1 && (
                <Pagination
                  currentPage={
                    useVerificationSteps ? verificationStep : currentPage
                  }
                  setCurrentPage={
                    useVerificationSteps
                      ? handleAppraisalVerificationStepChange
                      : setCurrentPage
                  }
                  totalPage={totalPage}
                  navigablePages={appraisalNavSequence}
                />
              )}
              <StyledSection height={selectedAgent?.verified ? 170 : 240}>
                {visibleAppraisalSections.length > 0 ? (
                  <AppraisalFields
                    values={formik.values}
                    formik={formik}
                    isVerified={!!selectedAgent?.verified}
                    sections={visibleAppraisalSections}
                  />
                ) : appraisalSections.length === 0 ? (
                  <div className="flex justify-center mt-5 text-sm text-gray-400">
                    No fields have been extracted for this document.
                  </div>
                ) : (
                  <div className="flex justify-center mt-5 text-sm text-gray-400">
                    No fields on this page — use the arrows above to navigate.
                  </div>
                )}
              </StyledSection>
            </>
          ) : (
            <>
              <Pagination
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPage={totalPage}
                navigablePages={bmaNavSequence}
              />
              <StyledSection height={selectedAgent?.verified ? 170 : 240}>
                {(() => {
                  // Find the page object where page.page matches the current PDF page (convert currentPage to 0-based)
                  const pdfPageIndex = currentPage - 1; // Convert 1-based currentPage to 0-based
                  
                  // First, try to find exact match
                  let currentPageData = initialValues?.find(
                    pageData => pageData?.page === pdfPageIndex
                  );
                  
                  // If not found, find the previous page (highest page.page value that is less than pdfPageIndex)
                  if (!currentPageData && initialValues) {
                    const previousPages = initialValues
                      .filter(pageData => pageData?.page !== undefined && pageData?.page !== null && pageData.page < pdfPageIndex)
                      .sort((a, b) => b.page - a.page); // Sort descending to get the highest page number
                    
                    if (previousPages.length > 0) {
                      currentPageData = previousPages[0];
                    }
                  }
                  
                  const currentPageSections = currentPageData?.sections || [];
                  const actualPageNumber = currentPageData?.page ?? pdfPageIndex;
                  
                  // For low confidence count, use the actual page number
                  const lowConfidenceCount = countLowConfidence[actualPageNumber] || 0;
                  
                  if (checkedLowConfidence && lowConfidenceCount === 0) {
                    return (
                      <div className="flex justify-center mt-5">
                        This page has no Low-Confidence fields
                      </div>
                    );
                  }
                  
                  return currentPageSections.map((item, key) => {
                    // Find the original page index for formik field paths
                    const originalPageIndex = initialValues?.findIndex(
                      pageData => pageData === currentPageData
                    );
                    
                    return (
                      <SectionItem
                        key={`page-${actualPageNumber}-section-${key}`}
                        sectionKey={key}
                        section={item}
                        isOpenSection={
                          checkedLowConfidence && !item.hasLowConfidence ? true : false
                        }
                        formik={formik}
                        currentPage={originalPageIndex >= 0 ? originalPageIndex + 1 : currentPage}
                        values={formik.values}
                        isVerified={!!selectedAgent?.verified}
                        selectedAgentId={selectedAgent.id}
                        checkedLowConfidence={checkedLowConfidence}
                        bmaVersion={bmaVersion}
                      />
                    );
                  });
                })()}
              </StyledSection>
            </>
          )
        ) : (
          <p className="mt-2 text-center" data-testid="test-message-locked">
            This document is being verified by another person.
          </p>
        ))}
      {!lockedByAnotherUser &&
        enableVerify &&
        !selectedAgent?.verified &&
        enableRenderInput && (
          <div className="flex justify-center">
            <div className="absolute mt-3 mb-5 flex justify-center">
              <button
                data-testid="test-discard-changes-button"
                type="submit"
                className="px-3 py-2 text-xs font-medium text-center text-blue-700 rounded-lg hover:text-white border border-blue-500 hover:bg-blue-500 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                onClick={() => handleDiscardChanges()}
              >
                Discard changes
              </button>
            </div>
          </div>
        )}
    </>
  );
};
Section.propTypes = {
  selectedAgent: PropTypes.object,
  currentPage: PropTypes.number,
  setCurrentPage: PropTypes.func,
  totalPage: PropTypes.number,
  lockedByAnotherUser: PropTypes.bool,
  setIsLocked: PropTypes.func,
};

export default Section;
